import { fetchGameMarquee } from 'api'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useToastAlert } from 'hooks/useToastAlert'
import CustomerServiceModal, { CustomerServiceItem } from './index'

interface OpenCustomerServiceModalOptions {
  direction?: 0 | 1 | 2
}

interface CustomerServiceModalContextValue {
  openCustomerServiceModal: (options?: OpenCustomerServiceModalOptions) => Promise<void>
  closeCustomerServiceModal: () => void
}

const CustomerServiceModalContext = createContext<CustomerServiceModalContextValue | null>(null)

export function CustomerServiceModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [direction, setDirection] = useState<0 | 1 | 2>(0)
  const [list, setList] = useState<CustomerServiceItem[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const { toast } = useToastAlert()

  const closeCustomerServiceModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleClickCustomerService = useCallback((item: CustomerServiceItem) => {
    setIsOpen(false)
    if (item?.url) {
      window.open(item.url)
    }
  }, [])

  const openCustomerServiceModal = useCallback(
    async ({ direction = 0 }: OpenCustomerServiceModalOptions = {}) => {
      setDirection(direction)

      let currentList = list
      if (!hasLoaded) {
        try {
          const data = await fetchGameMarquee()
          currentList = [...(data.customerServiceUrl ?? [])]
          setList(currentList)
          setHasLoaded(true)
        } catch (ex) {
          console.warn(ex)
          toast('目前暂无客服人员')
          return
        }
      }

      if (currentList.length === 0) {
        toast('目前暂无客服人员')
        return
      }

      setIsOpen(true)
    },
    [hasLoaded, list, toast]
  )

  const value = useMemo<CustomerServiceModalContextValue>(
    () => ({
      openCustomerServiceModal,
      closeCustomerServiceModal,
    }),
    [closeCustomerServiceModal, openCustomerServiceModal]
  )

  return (
    <CustomerServiceModalContext.Provider value={value}>
      {children}
      <CustomerServiceModal
        isOpen={isOpen}
        onClose={closeCustomerServiceModal}
        list={list}
        onClick={handleClickCustomerService}
        direction={direction}
      />
    </CustomerServiceModalContext.Provider>
  )
}

export function useCustomerServiceModal() {
  const ctx = useContext(CustomerServiceModalContext)
  if (ctx == null) {
    throw new Error('useCustomerServiceModal must be used within CustomerServiceModalProvider')
  }
  return ctx
}
